import { addBrand, type Brand } from "@/store/slices/brands/brandsSlice";
import { setBrandsMappingForMarket } from "@/store/slices/brandsOrderMapping/brandsOrderMappingSlice";
import type { AppDispatch } from "@/store/store";
import type { BrandSetView } from "@/components/BrandSets/shared/brandSet.types";

interface AdoptOptions {
  dispatch: AppDispatch;
  brandsOrderMapping: Record<string, (number | string)[]>;
  /** Store brand entities, used only to enrich member logo/name when the view omits them. */
  brandEntities: Record<number | string, Brand>;
}

/**
 * Insert a cloned/owned brand set into the store exactly as the create flow does:
 * a BRAND_SET entity via addBrand, then its ULID appended to every market's order.
 * A DISABLED set (inaccessible members) gets empty marketIds and no order append,
 * mirroring the create-DISABLED branch in useBrandSetForm.
 */
export function adoptBrandSetIntoStore(
  view: BrandSetView,
  opts: AdoptOptions,
): Brand {
  const disabled = view.status === "DISABLED";

  const members = (view.members ?? []).map((m) => {
    const existing = opts.brandEntities[m.id];
    return {
      id: m.id,
      weight: m.weight ?? 1,
      logo: m.logo ?? existing?.logo ?? null,
      name: m.displayName || m.name || existing?.displayName || existing?.name || "",
    };
  });

  const marketIds = disabled ? [] : (view.markets ?? []).map((mk) => mk.marketId);

  const brand: Brand = {
    id: view.ulid,
    name: view.name,
    logo: "",
    displayName: view.name,
    position: null,
    color: view.color ?? null,
    marketIds,
    reputationMarketIds: [],
    type: "BRAND_SET",
    brandValues: [],
    description: view.description ?? "",
    ulid: view.ulid,
    weighted: view.weighted ?? false,
    members,
  };

  opts.dispatch(addBrand(brand));

  if (!disabled) {
    Object.keys(opts.brandsOrderMapping).forEach((marketId) => {
      const currentOrder = opts.brandsOrderMapping[marketId] || [];
      if (!currentOrder.includes(view.ulid)) {
        opts.dispatch(
          setBrandsMappingForMarket({
            marketId: Number(marketId),
            order: [...currentOrder, view.ulid],
          }),
        );
      }
    });
  }

  return brand;
}
